/**
 * „Absenden nach Stille" im Blatt der Antwortlänge — samt dem Stand von Auto.
 * (29.09.2026)
 *
 * Martin, 29.09. ~22:00: *„Vielleicht kann man die Einstellung auch noch in
 * das Feld einbauen, wo die Kurz-, Mittel- und Lang-Ausgabe ist: ob der
 * Auto-Modus an sein soll oder nicht und inwieweit er jetzt schon aktiv
 * ist."*
 *
 * ⚠️ Seit 22.10.2026 NUR ANZEIGE (Martin: „verstehe ich nicht, weil wir in den
 *    Einstellungen doch etwas anderes haben“): die Zeile nennt, was gilt, in den
 *    Worten der Einstellungsseite, und führt dorthin — gewählt wird nur noch
 *    dort (siehe baueStilleZeile). Darunter, solange Jev entscheidet, ein
 *    ruhiger Satz:
 *
 *   - Auto gewählt: was heute geschah („12× sofort abgeschickt, 3× länger
 *     gewartet") — oder dass Jev gerade nicht antwortet und die feste Frist
 *     gilt. Die Zahlen zählt der Server (server/stille-auto-zaehler.ts);
 *     die Worte stehen in shared/stille-auto.ts (autoStandZeile).
 *   - Sonst kein Satz — der Wert oben in der Zeile sagt es.
 *
 * ⚠️ Nicht-Betreiber sehen von Auto NICHTS — die App fragt den Stand für sie
 *    gar nicht erst ab (der Server antwortete ohnehin 403).
 * ⚠️ EIN Besitzer: `sendeStilleMs`, EINE Wahl (Einstellungen). Die Zeile
 *    schreibt nichts; gezeichnet wird bei jedem Mal aus dem
 *    Einstellungs-Zwischenspeicher.
 * ⚠️ Der Stand darf nie etwas blockieren: scheitert die Abfrage oder dauert
 *    sie zu lang, steht „Stand gerade nicht abrufbar" — der Weg zu den
 *    Einstellungen bleibt bedienbar.
 */
import {
  autoStandZeile,
  istAutoStand,
  STILLE_AUTO_MS,
  STILLE_AUTO_STAND_PFAD,
  type AutoStand,
  type AutoStandLage,
} from "../../shared/stille-auto.js";
import { loadSettings } from "./settings.js";
import { ABSENDEN_NAME, STILLE_WAHL_NAME, stilleAutoMoeglich, stilleStandWort } from "./stille-wahl.js";

/** Der feste Satz unter dem Namen, wo es keinen Stand von Auto gibt (alle Konten außer 1). */
const STILLE_ERKLAERUNG = "So lange wartest du still, bevor dein Satz abgeht.";

/** So lange wartet das Blatt auf den Stand, dann heißt es „nicht abrufbar". */
const STAND_FRIST_MS = 4000;

/** Der Stand von heute — `null` bei jedem Ausfall (kein Konto 1, kein Netz, Frist, Unsinn). */
async function holeAutoStand(): Promise<AutoStand | null> {
  const stopp = new AbortController();
  const wecker = window.setTimeout(() => stopp.abort(), STAND_FRIST_MS);
  try {
    const r = await fetch(STILLE_AUTO_STAND_PFAD, {
      credentials: "same-origin",
      cache: "no-store",
      signal: stopp.signal,
    });
    if (!r.ok) return null;
    const d: unknown = await r.json();
    return istAutoStand(d) ? d : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(wecker);
  }
}

interface StilleZeile {
  el: HTMLElement;
  /** Aus dem Einstellungs-Zwischenspeicher neu zeichnen. */
  zeichne(): void;
}

/**
 * Die Zeile bauen — NUR Anzeige (22.10.2026). `geheZuEinstellungen` schließt das Blatt und öffnet die Seite, auf der
 * die Wahl wohnt.
 *
 * Martin, 22.10. (Freigabe des neuen Blatts, mit Auflage): *„Das mit dem Absenden nach Stille verstehe ich nicht,
 * weil wir in den Einstellungen doch etwas anderes haben.“* Belegt: beide schrieben dasselbe Feld `sendeStilleMs`,
 * aber mit VERSCHIEDENEN Wahlen — hier Aus · 2 · 3 · 5 · 10 · 30 · 60 s · Auto, in den Einstellungen (Konto 1) der
 * Regler „Automatisch absenden“ mit Jev entscheidet · Feste Stille 2/3/5 s · Aus. 10, 30 und 60 s gab es nur hier.
 * Darum gibt es die Wahl nur noch an EINER Stelle (Einstellungen → Sprechen → Von selbst absenden); das Blatt zeigt,
 * was gilt, mit denselben Worten, und führt dorthin. An der Absende-Logik ändert das nichts.
 */
export function baueStilleZeile(geheZuEinstellungen: () => void): StilleZeile {
  const mitAuto = stilleAutoMoeglich();
  /** Der zuletzt gelesene Stand von heute — das Einzige, was die Zeile selbst hält. */
  let lage: AutoStandLage = "laedt";
  const el = document.createElement("div");
  el.className = "schnell-einst-zeile";
  el.dataset.zeile = "stille";

  const kopf = document.createElement("div");
  kopf.className = "schnell-einst-kopf";
  const name = document.createElement("span");
  name.className = "schnell-einst-name";
  name.id = "schnell-stille-name";
  // Derselbe Name wie auf der Einstellungsseite — sonst sucht man dort etwas anderes.
  name.textContent = mitAuto ? ABSENDEN_NAME : STILLE_WAHL_NAME;
  const wert = document.createElement("span");
  wert.className = "schnell-einst-wert";
  wert.dataset.stilleWert = "";
  kopf.append(name, wert);

  // Der Weg zur EINEN Wahl: ein leiser Textknopf, der sagt, was er tut.
  const weg = document.createElement("button");
  weg.type = "button";
  weg.className = "schnell-einst-weg";
  weg.textContent = "In den Einstellungen ändern";
  weg.setAttribute("aria-describedby", name.id);
  weg.addEventListener("click", () => geheZuEinstellungen());

  // Nur für das Betreiberkonto und nur, solange Jev entscheidet: der ruhige Satz, was Auto heute tat. Reiner Text.
  const satz = document.createElement("p");
  satz.className = "schnell-einst-stand";
  satz.id = "schnell-stille-stand";
  satz.setAttribute("role", "status");
  satz.hidden = true;

  el.append(kopf);
  // Den Satz gibt es nur, wo Auto zur Wahl steht — sonst weder Element noch Ansage; dort steht der feste Satz.
  if (mitAuto) el.append(satz);
  else {
    const erklaert = document.createElement("p");
    erklaert.className = "schnell-einst-hinweis";
    erklaert.textContent = STILLE_ERKLAERUNG;
    el.append(erklaert);
  }
  el.append(weg);

  function lade(): void {
    lage = "laedt";
    void holeAutoStand().then((s) => {
      lage = s ?? "fehlt";
      zeichne();
    });
  }

  function zeichne(): void {
    const ms = Number(loadSettings().sendeStilleMs ?? 0);
    wert.textContent = stilleStandWort(ms, mitAuto);
    const an = mitAuto && ms === STILLE_AUTO_MS;
    satz.hidden = !an;
    if (!an) return;
    satz.textContent = autoStandZeile(ms, lage);
    satz.dataset.lage = typeof lage === "object" && lage.stoerung ? "stoerung" : "an";
  }

  if (mitAuto) lade();
  zeichne();
  return { el, zeichne };
}
